import { HttpService } from "../../modules/shared/services/http.service";
import { 
  Invoice, 
  InvoiceCreateDto, 
  InvoiceUpdateDto,
  InvoiceResponse
} from "../../modules/shared/model";

const headers = { "Content-Type": "application/json", "x-custom-lang": "en" } as const;

// Get all invoices with pagination
export const getInvoices = async (query?: Record<string, string | number | boolean>): Promise<InvoiceResponse> => {
  const qs = new URLSearchParams();
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qs.append(k, String(v));
  });
  const response = await HttpService.get(`api/v1/invoices${qs.toString() ? `?${qs}` : ""}`, true, { "x-custom-lang": "en" });
  return response;
};

// Get invoice by ID
export const getInvoiceById = async (id: string): Promise<Invoice> => {
  const response = await HttpService.get(`api/v1/invoices/${id}`, true, { "x-custom-lang": "en" });
  return response;
};

// Create new invoice
export const createInvoiceAction = async (data: InvoiceCreateDto): Promise<Invoice> => {
  const response = await HttpService.post("api/v1/invoices", data, headers, true);
  return response;
};

// Update invoice
export const updateInvoiceAction = async (id: string, data: InvoiceUpdateDto): Promise<Invoice> => {
  const response = await HttpService.patch(`api/v1/invoices/${id}`, data, headers, true);
  return response;
};

// Delete invoice
export const deleteInvoiceAction = async (id: string): Promise<void> => {
  await HttpService.deleteRecord(`api/v1/invoices/${id}`, { "x-custom-lang": "en" }, true);
};

// Bulk create invoices
export const bulkCreateInvoicesAction = async (invoices: InvoiceCreateDto[]): Promise<Invoice[]> => {
  const response = await HttpService.post("api/v1/invoices/bulk", { invoices }, headers, true);
  return response;
};

// Bulk update invoices
export const bulkUpdateInvoicesAction = async (invoices: Array<{ id: string; data: InvoiceUpdateDto }>): Promise<Invoice[]> => {
  const response = await HttpService.patch("api/v1/invoices/bulk", { invoices }, headers, true);
  return response;
};

// Download invoice PDF (from Zoho)
export const downloadInvoicePdf = async (id: string): Promise<Blob> => {
  const response = await HttpService.getBlob(`api/v1/invoices/${id}/download`, true, { "x-custom-lang": "en" });
  return response;
};

// Get invoice PDF URL (from Zoho)
export const getInvoicePdfUrl = async (id: string): Promise<{ pdfUrl: string }> => {
  const response = await HttpService.get(`api/v1/invoices/${id}/pdf-url`, true, { "x-custom-lang": "en" });
  return response;
};

// Zoho Token Management APIs

export interface ZohoTokenInfo {
  hasToken: boolean;
  isExpired: boolean;
  expiresAt: string | null;
  expiresIn: number | null;
  configured: boolean;
}

export interface ZohoRefreshResponse {
  success: boolean;
  message: string;
  tokenInfo: ZohoTokenInfo;
}

export interface ZohoConnectionTestResponse {
  success: boolean;
  message: string;
  zohoEnabled: boolean;
}

export interface UpdateTokensDto {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
}

// Get Zoho token status
export const getZohoTokenStatus = async (): Promise<ZohoTokenInfo> => {
  const response = await HttpService.get("api/v1/zoho-tokens/status", true, { "x-custom-lang": "en" });
  return response;
};

// Manually refresh Zoho token
export const refreshZohoToken = async (): Promise<ZohoRefreshResponse> => {
  const response = await HttpService.get("api/v1/zoho-tokens/refresh", true, { "x-custom-lang": "en" });
  return response;
};

// Update Zoho tokens manually
export const updateZohoTokens = async (data: UpdateTokensDto): Promise<ZohoRefreshResponse> => {
  const response = await HttpService.post("api/v1/zoho-tokens/update", data, headers, true);
  return response;
};

// Test Zoho API connection
export const testZohoConnection = async (): Promise<ZohoConnectionTestResponse> => {
  const response = await HttpService.get("api/v1/zoho-tokens/test-connection", true, { "x-custom-lang": "en" });
  return response;
};

// Get refresh token info
export const getZohoRefreshToken = async (): Promise<{ refreshToken: string | null; configured: boolean }> => {
  const response = await HttpService.get("api/v1/zoho-tokens/refresh-token", true, { "x-custom-lang": "en" });
  return response;
};

// Debug invoice data (for troubleshooting)
export const debugInvoiceData = async (id: string): Promise<any> => {
  const response = await HttpService.get(`api/v1/invoices/${id}/debug`, true, { "x-custom-lang": "en" });
  return response;
};

// Get Zoho invoice URL for external viewing
export const getZohoInvoiceUrl = (invoiceId: string, zohoInvoicePdfUrl?: string): string => {
  if (zohoInvoicePdfUrl) {
    return zohoInvoicePdfUrl;
  }
  const orgId = process.env.REACT_APP_ZOHO_ORGANIZATION_ID;
  return `https://books.zoho.com/app#/books/${orgId}/invoice/${invoiceId}`;
};

// Get unique invoice names for filtering
export const getUniqueInvoiceNames = async (): Promise<string[]> => {
  const response = await HttpService.get("api/v1/invoices/unique/names", true, { "x-custom-lang": "en" });
  return response;
};
